import { useCallback, useMemo } from 'react';
import { resetDraftSettings, clearDraftAndSavedSettings, updateDraftSetting, updateSettings, addSection, removeSection, updateDraftSettingOrders } from './sectionSettingsSlice';
import { useFastDispatch, useFastStore } from '../context/FastContext';
import { QUICK_ACTIONS_SECTION_KEY } from '../../../sections/actions/container/types';
export const useClearDraftAndSavedSettings = () => {
  const dispatch = useFastDispatch();
  return useCallback(() => {
    dispatch(clearDraftAndSavedSettings());
  }, [dispatch]);
};
export const useResetDraftSettings = () => {
  const dispatch = useFastDispatch();
  return useCallback(() => {
    dispatch(resetDraftSettings());
  }, [dispatch]);
};
export const useDraftSettings = () => {
  const draft = useFastStore(store => store.sectionSettings.draftSettings);
  return useMemo(() => {
    return draft;
  }, [draft]);
};
export const useDraftSettingBySectionKey = sectionKey => {
  const draft = useFastStore(store => store.sectionSettings.draftSettings);
  const setting = useMemo(() => draft.find(item => item.sectionKey === sectionKey), [draft, sectionKey]);
  return useMemo(() => {
    return setting;
  }, [setting]);
};
export const useSettings = () => {
  const draft = useFastStore(store => store.sectionSettings.settings);
  return useMemo(() => {
    return draft;
  }, [draft]);
};
const EMPTY_SETTINGS = {};
export const useResetDraftSettingBySectionKey = sectionKey => {
  var _settings$find;
  const settings = useSettings();
  const settingsForSectionKey = ((_settings$find = settings.find(item => item.sectionKey === sectionKey)) === null || _settings$find === void 0 ? void 0 : _settings$find.settings) || EMPTY_SETTINGS;
  const dispatch = useFastDispatch();
  const draftSetting = useDraftSettingBySectionKey(sectionKey);
  const draftSettingSettings = (draftSetting === null || draftSetting === void 0 ? void 0 : draftSetting.settings) || EMPTY_SETTINGS;
  return useCallback(() => {
    return dispatch(updateDraftSetting({
      sectionKey,
      settings: Object.assign({}, draftSettingSettings, settingsForSectionKey)
    }));
  }, [dispatch, draftSettingSettings, sectionKey, settingsForSectionKey]);
};
export const useHasChanges = () => {
  const settings = useSettings();
  const draftSettings = useDraftSettings();
  return useMemo(() => {
    const hasSectionsAddedOrRemoved = settings.length !== draftSettings.length;
    if (hasSectionsAddedOrRemoved) {
      return true;
    }
    const hasSectionsOrderChanged = settings.some((item, index) => item.sectionKey !== draftSettings[index].sectionKey);
    if (hasSectionsOrderChanged) {
      return true;
    }

    //handle quick actions change
    const quickActionSettings = settings.find(x => x.sectionKey === QUICK_ACTIONS_SECTION_KEY);
    const draftQuickActionSettings = draftSettings.find(x => x.sectionKey === QUICK_ACTIONS_SECTION_KEY);
    if (quickActionSettings && !draftQuickActionSettings) {
      return true;
    }
    if (!quickActionSettings && draftQuickActionSettings) {
      return true;
    }
    if (quickActionSettings !== null && quickActionSettings !== void 0 && quickActionSettings.settings && draftQuickActionSettings !== null && draftQuickActionSettings !== void 0 && draftQuickActionSettings.settings) {
      //different length
      if (quickActionSettings.settings.quickActions.length !== draftQuickActionSettings.settings.quickActions.length) {
        return true;
      }
      const orderChanged = quickActionSettings.settings.quickActions.some((item, index) => item.type !== draftQuickActionSettings.settings.quickActions[index].type);
      if (orderChanged) {
        return true;
      }
    }
  }, [settings, draftSettings]);
};
export const useHasChangesBySectionKey = sectionKey => {
  const settings = useSettings();
  const draftSettings = useDraftSettings();
  return useMemo(() => {
    var _settings$find2, _draftSettings$find;
    const settingForSection = (_settings$find2 = settings.find(item => item.sectionKey === sectionKey)) === null || _settings$find2 === void 0 ? void 0 : _settings$find2.settings;
    const draftSettingForSection = (_draftSettings$find = draftSettings.find(item => item.sectionKey === sectionKey)) === null || _draftSettings$find === void 0 ? void 0 : _draftSettings$find.settings;
    return JSON.stringify(settingForSection) !== JSON.stringify(draftSettingForSection);
  }, [settings, draftSettings, sectionKey]);
};
export const useIsValidBySectionKey = (sectionKey, validate) => {
  const draftSettings = useDraftSettings();
  return useMemo(() => {
    var _draftSettings$find2;
    const draftSettingForSection = (_draftSettings$find2 = draftSettings.find(item => item.sectionKey === sectionKey)) === null || _draftSettings$find2 === void 0 ? void 0 : _draftSettings$find2.settings;
    return validate && draftSettingForSection ? validate(draftSettingForSection) : true;
  }, [draftSettings, sectionKey, validate]);
};
export const useUpdateDraftSetting = sectionKey => {
  const dispatch = useFastDispatch();
  const draftSetting = useDraftSettingBySectionKey(sectionKey);
  return useCallback(updatedSettings => {
    return dispatch(updateDraftSetting(Object.assign({}, draftSetting, {
      settings: Object.assign({}, (draftSetting === null || draftSetting === void 0 ? void 0 : draftSetting.settings) || {}, updatedSettings)
    })));
  }, [dispatch, draftSetting]);
};
export const useUpdateDraftSettingOrders = () => {
  const dispatch = useFastDispatch();
  return useCallback(sectionOrders => {
    return dispatch(updateDraftSettingOrders(sectionOrders));
  }, [dispatch]);
};

// To set settings/draftSettings any time DFC cache is updated
export const useUpdateSettings = () => {
  const dispatch = useFastDispatch();
  return useCallback(settings => {
    return dispatch(updateSettings(settings));
  }, [dispatch]);
};
export const useAddSection = () => {
  const dispatch = useFastDispatch();
  return useCallback((sectionKey, defaultSetting) => {
    return dispatch(addSection({
      sectionKey,
      defaultSetting
    }));
  }, [dispatch]);
};
export const useRemoveSection = () => {
  const dispatch = useFastDispatch();
  return useCallback(sectionKey => {
    return dispatch(removeSection(sectionKey));
  }, [dispatch]);
};